import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin } from "lucide-react";
import api, { API } from "@/lib/api";
import SEO from "@/components/SEO";

/**
 * Phase 4.4 / 4.7 — Public collector registry with live search.
 */
export default function CollectorsRegistry() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/public/collectors/search?q=${encodeURIComponent(term)}`);
        setResults(r.data.results || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="container-gm py-14 max-w-5xl" data-testid="collectors-registry">
      <SEO title="Collector Registry" />
      <div className="kicker kicker-red">Collector Registry</div>
      <h1 className="h-display text-4xl md:text-5xl mt-2">Meet the collectors.</h1>
      <p className="mt-3 max-w-xl text-[var(--smoke)]">
        Search by username, display name, or Public Collector ID (e.g. GM-C000042).
        Only collectors who&apos;ve made their profile public appear here.
      </p>

      <div className="mt-8 relative max-w-md">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search collectors…"
          data-testid="collectors-search"
          className="gm-field pl-11"
          autoFocus
        />
      </div>

      <div className="mt-8" data-testid="collectors-results">
        {loading && <div className="text-[var(--smoke)] text-sm">Searching…</div>}
        {!loading && q && results.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-[var(--line)] bg-white p-8 text-center text-[var(--smoke)]"
                data-testid="collectors-empty">
            No public collectors match &ldquo;{q}&rdquo;.
          </div>
        )}
        {results.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {results.map((c) => <CollectorCard key={c.username} c={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function CollectorCard({ c }) {
  const avatarUrl = c.avatar_media_id
    ? `${API}/public/collectors/${c.username}/avatar?v=${c.avatar_updated_at || ""}`
    : null;
  return (
    <Link
      to={`/collectors/${c.username}`}
      data-testid={`collector-card-${c.username}`}
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 flex items-center gap-4 shadow-[3px_3px_0_var(--ink)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition"
    >
      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] grid place-items-center shrink-0">
        {avatarUrl
          ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
          : <span className="font-display text-lg">{(c.display_name || "?").charAt(0).toUpperCase()}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[var(--ink)] truncate">{c.display_name}</div>
        <div className="text-xs text-[var(--smoke)] flex items-center gap-2 mt-0.5">
          <span>@{c.username}</span>
          {c.public_collector_id && (
            <span className="font-mono text-[var(--panini-red)] font-bold">{c.public_collector_id}</span>
          )}
          {c.country && <span className="inline-flex items-center gap-1"><MapPin size={12} />{c.country}</span>}
        </div>
      </div>
    </Link>
  );
}
